/**
 * Pipe estimating & takeoff engine.
 *
 * Turns a Pipe Sketch takeoff (cut list + fittings + valves + supports + terminations)
 * into labor hours and material dollars.
 *
 * Everything here is deterministic and ships in the bundle — no network, no lookups.
 * The labor units are industry-typical field units for an experienced crew; the
 * material prices are seed values. BOTH are editable per line in the UI and the
 * overrides are stored on the device.
 */

import {
  getSizeSpec,
  type ComponentKind,
  type MaterialId,
  type SupportKind,
  type TerminationKind,
} from "@/lib/pipe-spec";
import type {
  ComponentPlacement,
  CutPiece,
  RunFitting,
  SupportPlacement,
  TerminationPlacement,
} from "@/hooks/use-pipe-sketch";

/* ------------------------------------------------------------------ */
/* settings                                                            */
/* ------------------------------------------------------------------ */

export interface EstimateSettings {
  /** fully burdened crew rate, $ per labor hour */
  laborRate: number;
  /** productivity factor — 1.00 = standard conditions, >1 = harder job */
  laborFactor: number;
  /** crew size used to convert hours into calendar days */
  crewSize: number;
  /** working hours in a shift */
  hoursPerDay: number;
  /** scrap / offcut allowance added to pipe footage, % */
  pipeWastePct: number;
  /** global material price adjustment, % (mill swings, region) */
  priceIndexPct: number;
  /** markup applied to material only, % */
  materialMarkupPct: number;
  /** sales tax on material, % */
  taxPct: number;
  /** overhead on labor + material, % */
  overheadPct: number;
  /** profit on the subtotal, % */
  profitPct: number;
  /** consumables (rod, gas, wheels, rags) as % of labor cost */
  consumablesPct: number;
}

export const DEFAULT_ESTIMATE_SETTINGS: EstimateSettings = {
  laborRate: 95,
  laborFactor: 1,
  crewSize: 2,
  hoursPerDay: 8,
  pipeWastePct: 5,
  priceIndexPct: 0,
  materialMarkupPct: 15,
  taxPct: 0,
  overheadPct: 10,
  profitPct: 10,
  consumablesPct: 4,
};

/** Per-line user overrides, keyed by the line key. */
export interface UnitOverride {
  unitHours?: number;
  unitCost?: number;
}
export type OverrideMap = Record<string, UnitOverride>;

/* ------------------------------------------------------------------ */
/* interpolation                                                       */
/* ------------------------------------------------------------------ */

type SizeTable = Record<number, number>;

/** Linear interpolation across a size-keyed table, clamped at both ends. */
function bySize(table: SizeTable, nom: number): number {
  const keys = Object.keys(table)
    .map(Number)
    .sort((a, b) => a - b);
  if (!keys.length) return 0;
  if (nom <= keys[0]) return table[keys[0]];
  if (nom >= keys[keys.length - 1]) return table[keys[keys.length - 1]];
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i];
    const b = keys[i + 1];
    if (nom >= a && nom <= b) {
      const t = (nom - a) / (b - a);
      return table[a] + t * (table[b] - table[a]);
    }
  }
  return table[keys[keys.length - 1]];
}

/* ------------------------------------------------------------------ */
/* labor unit tables (crew hours)                                      */
/* ------------------------------------------------------------------ */

/** Handle, rig and erect straight pipe — hours per linear foot. */
const PIPE_PER_FT: SizeTable = {
  0.5: 0.1,
  0.75: 0.11,
  1: 0.12,
  1.25: 0.14,
  1.5: 0.15,
  2: 0.17,
  2.5: 0.21,
  3: 0.25,
  4: 0.31,
  5: 0.38,
  6: 0.44,
  8: 0.57,
  10: 0.72,
  12: 0.86,
  14: 0.99,
  16: 1.14,
  18: 1.3,
  20: 1.46,
  24: 1.8,
};

/** Full-penetration field butt weld including fit-up and tack — hours per joint. */
const BW_JOINT: SizeTable = {
  0.5: 0.7,
  0.75: 0.8,
  1: 0.9,
  1.25: 1.0,
  1.5: 1.1,
  2: 1.3,
  2.5: 1.6,
  3: 1.9,
  4: 2.5,
  5: 3.05,
  6: 3.6,
  8: 4.8,
  10: 6.2,
  12: 7.5,
  14: 8.7,
  16: 10,
  18: 11.5,
  20: 13,
  24: 16,
};

/** Cut, ream, clean, flux and solder — hours per joint. */
const SOLDER_JOINT: SizeTable = {
  0.5: 0.18,
  0.75: 0.2,
  1: 0.24,
  1.25: 0.3,
  1.5: 0.35,
  2: 0.45,
  2.5: 0.6,
  3: 0.75,
  4: 1.05,
  6: 1.7,
  8: 2.5,
};

/** Cut, deburr, prime and solvent weld — hours per joint. */
const SOLVENT_JOINT: SizeTable = {
  0.5: 0.1,
  0.75: 0.11,
  1: 0.13,
  1.25: 0.16,
  1.5: 0.18,
  2: 0.22,
  2.5: 0.3,
  3: 0.36,
  4: 0.5,
  6: 0.8,
  8: 1.15,
  10: 1.5,
  12: 1.9,
};

/** Handle and set a fitting, excluding its joints — hours each. */
const FITTING_SET: SizeTable = {
  0.5: 0.15,
  1: 0.2,
  2: 0.3,
  3: 0.45,
  4: 0.6,
  6: 0.9,
  8: 1.25,
  10: 1.6,
  12: 2.0,
  16: 2.8,
  20: 3.6,
  24: 4.4,
};

/** Rig, set and bolt up a valve or specialty, excluding pipe joints — hours each. */
const VALVE_SET: SizeTable = {
  0.5: 0.5,
  1: 0.7,
  2: 1.1,
  3: 1.6,
  4: 2.2,
  6: 3.4,
  8: 4.8,
  10: 6.2,
  12: 7.6,
  16: 10.5,
  20: 13.5,
  24: 17,
};

/** Lay out, drill, hang and adjust a support — hours each. */
const SUPPORT_SET: SizeTable = {
  0.5: 0.6,
  1: 0.7,
  2: 0.9,
  3: 1.1,
  4: 1.4,
  6: 1.9,
  8: 2.5,
  10: 3.1,
  12: 3.8,
  16: 5.0,
  20: 6.2,
  24: 7.5,
};

/* multipliers ------------------------------------------------------- */

const FITTING_LABOR_MULT: Record<string, number> = {
  elbow90: 1,
  elbow45: 1,
  tee: 1.4,
  reducer: 0.9,
};

const COMPONENT_LABOR_MULT: Record<ComponentKind, number> = {
  gate: 1,
  ball: 0.9,
  globe: 1.05,
  check: 0.85,
  butterfly: 0.8,
  flangePair: 0.6,
  blindFlange: 0.5,
  union: 0.4,
  strainer: 1.1,
};

const SUPPORT_LABOR_MULT: Record<SupportKind, number> = {
  hanger: 1,
  trapeze: 2.1,
  guide: 1.3,
  anchor: 1.8,
  spring: 1.6,
  shoe: 1.4,
  roller: 1.5,
  riserClamp: 0.9,
};

const TERMINATION_LABOR_MULT: Record<TerminationKind, number> = {
  cap: 0.5,
  flangedEnd: 0.7,
  blindEnd: 0.7,
  pumpNozzle: 1.6,
  vesselNozzle: 1.4,
  exchangerNozzle: 1.4,
  tankNozzle: 1.4,
  equipment: 1.5,
  openEnd: 0.1,
  futureConn: 0.3,
};

/** Welding stainless is slower; heavy wall is slower. */
const JOINT_LABOR_MULT: Record<MaterialId, number> = {
  "cs-a106-sch40": 1,
  "cs-a106-sch80": 1.25,
  "ss-304-sch10s": 1.45,
  "ss-304-sch40s": 1.7,
  "cu-type-l": 1,
  "pvc-sch40": 1,
};

/* ------------------------------------------------------------------ */
/* material price seeds                                                */
/* ------------------------------------------------------------------ */

/** Pipe is priced off weight — $ per pound of pipe. */
const PIPE_PRICE_PER_LB: Record<MaterialId, number> = {
  "cs-a106-sch40": 1.55,
  "cs-a106-sch80": 1.65,
  "ss-304-sch10s": 5.25,
  "ss-304-sch40s": 5.0,
  "cu-type-l": 9.75,
  "pvc-sch40": 3.5,
};

/** Fittings / valves scale off the carbon steel seed price. */
const MATERIAL_PRICE_MULT: Record<MaterialId, number> = {
  "cs-a106-sch40": 1,
  "cs-a106-sch80": 1.35,
  "ss-304-sch10s": 3.4,
  "ss-304-sch40s": 3.8,
  "cu-type-l": 3.0,
  "pvc-sch40": 0.5,
};

const FITTING_PRICE_COEF: Record<string, number> = {
  elbow90: 1.6,
  elbow45: 1.4,
  tee: 2.6,
  reducer: 1.8,
};

const COMPONENT_PRICE: Record<ComponentKind, { c: number; base: number }> = {
  gate: { c: 30, base: 60 },
  ball: { c: 26, base: 55 },
  globe: { c: 38, base: 70 },
  check: { c: 24, base: 50 },
  butterfly: { c: 9, base: 90 },
  flangePair: { c: 6, base: 30 },
  blindFlange: { c: 4.5, base: 20 },
  union: { c: 3, base: 12 },
  strainer: { c: 22, base: 60 },
};

const SUPPORT_PRICE: Record<SupportKind, { c: number; base: number }> = {
  hanger: { c: 3.5, base: 12 },
  trapeze: { c: 9, base: 55 },
  guide: { c: 14, base: 60 },
  anchor: { c: 10, base: 45 },
  spring: { c: 45, base: 220 },
  shoe: { c: 12, base: 40 },
  roller: { c: 16, base: 70 },
  riserClamp: { c: 8, base: 25 },
};

/** Terminations that belong to somebody else's scope carry no material cost. */
const TERMINATION_PRICE: Record<TerminationKind, { c: number; base: number }> = {
  cap: { c: 3, base: 8 },
  flangedEnd: { c: 6, base: 30 },
  blindEnd: { c: 5, base: 25 },
  pumpNozzle: { c: 0, base: 0 },
  vesselNozzle: { c: 0, base: 0 },
  exchangerNozzle: { c: 0, base: 0 },
  tankNozzle: { c: 0, base: 0 },
  equipment: { c: 0, base: 0 },
  openEnd: { c: 0, base: 0 },
  futureConn: { c: 0, base: 0 },
};

/* ------------------------------------------------------------------ */
/* line items                                                          */
/* ------------------------------------------------------------------ */

export type EstimateSection =
  | "Pipe"
  | "Joints"
  | "Fittings"
  | "Valves & specialties"
  | "Supports"
  | "Terminations";

export const ESTIMATE_SECTIONS: EstimateSection[] = [
  "Pipe",
  "Joints",
  "Fittings",
  "Valves & specialties",
  "Supports",
  "Terminations",
];

export interface EstimateLine {
  /** stable key used for per-line overrides */
  key: string;
  section: EstimateSection;
  description: string;
  size: string;
  qty: number;
  unit: string;
  /** default labor hours per unit before any override */
  baseHours: number;
  /** default material $ per unit before any override */
  baseCost: number;
  /** effective values after overrides + settings */
  unitHours: number;
  unitCost: number;
  hours: number;
  material: number;
  labor: number;
  total: number;
  edited: boolean;
}

export interface EstimateTotals {
  hours: number;
  laborCost: number;
  consumables: number;
  materialBase: number;
  markup: number;
  tax: number;
  materialCost: number;
  subtotal: number;
  overhead: number;
  profit: number;
  grandTotal: number;
  crewDays: number;
  costPerFoot: number;
  totalFeet: number;
}

export interface EstimateResult {
  lines: EstimateLine[];
  totals: EstimateTotals;
}

export interface EstimateInput {
  material: MaterialId;
  cutList: CutPiece[];
  fittings: RunFitting[];
  placements: ComponentPlacement[];
  supports: SupportPlacement[];
  terminations: TerminationPlacement[];
  settings: EstimateSettings;
  overrides: OverrideMap;
}

/* ------------------------------------------------------------------ */

const jointTable = (material: MaterialId): SizeTable => {
  if (material === "cu-type-l") return SOLDER_JOINT;
  if (material === "pvc-sch40") return SOLVENT_JOINT;
  return BW_JOINT;
};

export function jointWord(material: MaterialId) {
  if (material === "cu-type-l") return "Soldered joint";
  if (material === "pvc-sch40") return "Solvent weld joint";
  return "Field butt weld";
}

interface Bucket {
  key: string;
  section: EstimateSection;
  description: string;
  size: string;
  unit: string;
  qty: number;
  baseHours: number;
  baseCost: number;
}

export function buildEstimate(input: EstimateInput): EstimateResult {
  const { material, cutList, fittings, placements, supports, terminations, settings, overrides } =
    input;

  const priceIndex = 1 + settings.priceIndexPct / 100;
  const famMult = MATERIAL_PRICE_MULT[material] ?? 1;
  const jointMult = JOINT_LABOR_MULT[material] ?? 1;
  const jt = jointTable(material);

  const buckets = new Map<string, Bucket>();
  const push = (b: Bucket) => {
    const existing = buckets.get(b.key);
    if (existing) existing.qty += b.qty;
    else buckets.set(b.key, { ...b });
  };

  /* --- pipe, by size ------------------------------------------------ */
  const waste = 1 + settings.pipeWastePct / 100;
  for (const p of cutList) {
    const spec = getSizeSpec(material, p.size);
    push({
      key: `pipe:${p.size}`,
      section: "Pipe",
      description: `${p.size} pipe`,
      size: p.size,
      unit: "ft",
      qty: (p.cutLength / 12) * waste,
      baseHours: bySize(PIPE_PER_FT, spec.nom),
      baseCost: spec.lbPerFt * (PIPE_PRICE_PER_LB[material] ?? 1.5),
    });
  }

  /* --- joints, by size ---------------------------------------------- */
  const addJoints = (size: string, n: number) => {
    if (n <= 0) return;
    const spec = getSizeSpec(material, size);
    push({
      key: `joint:${size}`,
      section: "Joints",
      description: `${size} ${jointWord(material).toLowerCase()}`,
      size,
      unit: "ea",
      qty: n,
      baseHours: bySize(jt, spec.nom) * jointMult,
      baseCost: 0,
    });
  };
  for (const f of fittings) addJoints(f.size, f.welds);
  for (const c of placements) addJoints(c.size, c.joints);
  for (const t of terminations) addJoints(t.size, t.joints);

  /* --- fittings ------------------------------------------------------ */
  for (const f of fittings) {
    const spec = getSizeSpec(material, f.size);
    const mult = FITTING_LABOR_MULT[f.kind] ?? 1;
    const coef = FITTING_PRICE_COEF[f.kind] ?? 1.6;
    push({
      key: `fit:${f.kind}:${f.size}${f.sizeOut ? `x${f.sizeOut}` : ""}`,
      section: "Fittings",
      description: f.label,
      size: f.size,
      unit: "ea",
      qty: 1,
      baseHours: bySize(FITTING_SET, spec.nom) * mult,
      baseCost: coef * Math.pow(spec.nom, 1.85) * famMult * priceIndex,
    });
  }

  /* --- valves & specialties ------------------------------------------ */
  for (const c of placements) {
    const spec = getSizeSpec(material, c.size);
    const price = COMPONENT_PRICE[c.kind] ?? { c: 20, base: 40 };
    push({
      key: `cmp:${c.kind}:${c.size}`,
      section: "Valves & specialties",
      description: `${c.size} ${c.label}`,
      size: c.size,
      unit: "ea",
      qty: 1,
      baseHours: bySize(VALVE_SET, spec.nom) * (COMPONENT_LABOR_MULT[c.kind] ?? 1),
      baseCost: (price.c * Math.pow(spec.nom, 1.75) + price.base) * famMult * priceIndex,
    });
  }

  /* --- supports ------------------------------------------------------- */
  for (const s of supports) {
    const spec = getSizeSpec(material, s.size);
    const price = SUPPORT_PRICE[s.kind] ?? { c: 4, base: 15 };
    push({
      key: `sup:${s.kind}:${s.size}`,
      section: "Supports",
      description: `${s.size} ${s.label}`,
      size: s.size,
      unit: "ea",
      qty: 1,
      baseHours: bySize(SUPPORT_SET, spec.nom) * (SUPPORT_LABOR_MULT[s.kind] ?? 1),
      baseCost: (price.c * spec.nom + price.base) * priceIndex,
    });
  }

  /* --- terminations ---------------------------------------------------- */
  for (const t of terminations) {
    const spec = getSizeSpec(material, t.size);
    const price = TERMINATION_PRICE[t.kind] ?? { c: 0, base: 0 };
    push({
      key: `term:${t.kind}:${t.size}`,
      section: "Terminations",
      description: `${t.size} ${t.label}`,
      size: t.size,
      unit: "ea",
      qty: 1,
      baseHours: bySize(VALVE_SET, spec.nom) * (TERMINATION_LABOR_MULT[t.kind] ?? 1),
      baseCost:
        price.c === 0 && price.base === 0
          ? 0
          : (price.c * Math.pow(spec.nom, 1.6) + price.base) * famMult * priceIndex,
    });
  }

  /* --- resolve ---------------------------------------------------------- */
  const order = new Map(ESTIMATE_SECTIONS.map((s, i) => [s, i]));
  const lines: EstimateLine[] = [...buckets.values()]
    .sort((a, b) => {
      const d = (order.get(a.section) ?? 9) - (order.get(b.section) ?? 9);
      return d !== 0 ? d : a.description.localeCompare(b.description);
    })
    .map((b) => {
      const ov = overrides[b.key];
      const unitHours = ov?.unitHours ?? b.baseHours;
      const unitCost = ov?.unitCost ?? b.baseCost;
      const hours = unitHours * b.qty * settings.laborFactor;
      const materialCost = unitCost * b.qty;
      const labor = hours * settings.laborRate;
      return {
        key: b.key,
        section: b.section,
        description: b.description,
        size: b.size,
        qty: b.qty,
        unit: b.unit,
        baseHours: b.baseHours,
        baseCost: b.baseCost,
        unitHours,
        unitCost,
        hours,
        material: materialCost,
        labor,
        total: labor + materialCost,
        edited: ov?.unitHours != null || ov?.unitCost != null,
      };
    });

  const hours = lines.reduce((s, l) => s + l.hours, 0);
  const laborCost = hours * settings.laborRate;
  const consumables = laborCost * (settings.consumablesPct / 100);
  const materialBase = lines.reduce((s, l) => s + l.material, 0);
  const markup = materialBase * (settings.materialMarkupPct / 100);
  const tax = (materialBase + markup) * (settings.taxPct / 100);
  const materialCost = materialBase + markup + tax;
  const subtotal = laborCost + consumables + materialCost;
  const overhead = subtotal * (settings.overheadPct / 100);
  const profit = (subtotal + overhead) * (settings.profitPct / 100);
  const grandTotal = subtotal + overhead + profit;
  const totalFeet = lines
    .filter((l) => l.section === "Pipe")
    .reduce((s, l) => s + l.qty, 0);

  return {
    lines,
    totals: {
      hours,
      laborCost,
      consumables,
      materialBase,
      markup,
      tax,
      materialCost,
      subtotal,
      overhead,
      profit,
      grandTotal,
      crewDays:
        settings.crewSize > 0 && settings.hoursPerDay > 0
          ? hours / (settings.crewSize * settings.hoursPerDay)
          : 0,
      costPerFoot: totalFeet > 0 ? grandTotal / totalFeet : 0,
      totalFeet,
    },
  };
}

export const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

export const hrs = (n: number) => `${n.toFixed(2)} hr`;
